import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type CancelRecord = {
  id: string;
  number?: string;
  title?: string;
  apPayeeName?: string | null;
  totalAmount?: string | null;
};

type ConfirmCancelModalContextType = {
  onOpenConfirmCancelModal: (record: CancelRecord) => void;
  onCloseConfirmCancelModal: () => void;
  cancelModalOpen: boolean;
  cancelRecord: CancelRecord | null;
  refetch: () => Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const Context = createContext<ConfirmCancelModalContextType>({
  onOpenConfirmCancelModal: () => {},
  onCloseConfirmCancelModal: () => {},
  cancelModalOpen: false,
  cancelRecord: null,
  refetch: () => Promise.resolve(),
  onDrawerClose: () => {},
  selectedId: null,
});

type ConfirmCancelModalProviderProps = PropsWithChildren<{
  refetch: () => Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
}>;

export const ConfirmCancelModalProvider: FC<
  ConfirmCancelModalProviderProps
> = ({ children, refetch, onDrawerClose, selectedId }) => {
  const [cancelRecord, setCancelRecord] = useState<CancelRecord | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const onOpenConfirmCancelModal = useCallback((record: CancelRecord) => {
    setCancelRecord(record);
    setCancelModalOpen(true);
  }, []);

  const onCloseConfirmCancelModal = useCallback(() => {
    setCancelModalOpen(false);
    setCancelRecord(null);
  }, []);

  const value = useMemo(
    () => ({
      onOpenConfirmCancelModal,
      onCloseConfirmCancelModal,
      cancelModalOpen,
      cancelRecord,
      refetch,
      onDrawerClose,
      selectedId,
    }),
    [
      onOpenConfirmCancelModal,
      onCloseConfirmCancelModal,
      cancelModalOpen,
      cancelRecord,
      refetch,
      onDrawerClose,
      selectedId,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmCancelModalProvider.displayName = 'ConfirmCancelModalProvider';

export const useConfirmCancelModal = () => useContext(Context);
